import React from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Pressable, type PressableProps, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

const defaultFilterButtonStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.075',
	color: 'color.text.subtle',
	borderRadius: token('radius.small'),
	paddingInline: 'space.150',
	paddingBlock: 'space.050',
	minHeight: '32px', // As specified on Figma
	maxWidth: '165px', // As specified on Figma
	backgroundColor: 'color.background.neutral.subtle',
	border: `${token('border.width')} solid ${token('color.border')}`,
	whiteSpace: 'nowrap',
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});

const compactContentFitStyles = xcss({
	maxWidth: '113px', // As specified on Figma
});

const minWidthZeroStyles = xcss({
	minWidth: '0', // Allow the button to shrink as much as possible
});

const filterButtonSelectedStateStyles = xcss({
	backgroundColor: 'color.background.selected',
	border: `${token('border.width')} solid ${token('color.border.selected')}`,
	color: 'color.text.selected',
	':hover': {
		backgroundColor: 'color.background.selected.hovered',
	},
});

const compactSpacingStyles = xcss({
	paddingInline: 'space.050',
});

const comfortableGapStyles = xcss({
	gap: 'space.100',
});

const filterButtonContentStyles = xcss({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const noBorderStyles = xcss({
	border: 0,
});

export type FilterButtonProps = {
	children?: React.ReactNode;
	isSelected?: boolean;
	iconBefore?: React.ReactNode;
	iconAfter?: React.ReactNode;
	/** Allow the button to shrink as much as possible */
	minWidthZero?: boolean;
	/** Controls the amount of padding in the button. */
	spacing?: 'default' | 'compact';
	/** Controls the amount of truncation (aka `maxWidth`) of the content */
	contentFit?: 'default' | 'compact';
	/** Controls the gap between the icon and the text */
	gap?: 'default' | 'comfortable';
	/** Controls whether or not to show border */
	noBorder?: boolean;
	/** Text to show in tooltip */
	tooltip?: React.ReactNode;
};

export const FilterButton: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<PressableProps & FilterButtonProps> & React.RefAttributes<HTMLButtonElement>
> = React.forwardRef<HTMLButtonElement, PressableProps & FilterButtonProps>(function FilterButton(
	{
		children,
		contentFit = 'default',
		iconAfter,
		iconBefore,
		isSelected,
		noBorder,
		tooltip,
		minWidthZero = false,
		spacing = 'default',
		gap = 'default',
		...pressableProps
	},
	ref,
) {
	return (
		<Tooltip content={tooltip ?? null} position="bottom">
			{(tooltipProps) => (
				<Box {...tooltipProps}>
					<Pressable
						{...pressableProps}
						xcss={[
							defaultFilterButtonStyles,
							isSelected && filterButtonSelectedStateStyles,
							spacing === 'compact' && compactSpacingStyles,
							contentFit === 'compact' && compactContentFitStyles,
							gap === 'comfortable' && comfortableGapStyles,
							minWidthZero && minWidthZeroStyles,
							noBorder && noBorderStyles,
						]}
						ref={ref}
					>
						{iconBefore}
						{children && <Box xcss={filterButtonContentStyles}>{children}</Box>}
						{iconAfter}
					</Pressable>
				</Box>
			)}
		</Tooltip>
	);
});
